import { useEffect, useState } from "react";
import type { LocalStore } from "@onchainreps/storage";
import { useAuth } from "../lib/useAuth";
import { isGoogleAuthEnabled, supabase } from "../lib/supabase";
import { ensureProfile, syncDrills, syncGames } from "../lib/sync";

interface AccountPanelProps {
  store: LocalStore | null;
}

export function AccountPanel({ store }: AccountPanelProps) {
  const auth = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [games, setGames] = useState(0);
  const [drills, setDrills] = useState(0);
  const [handle, setHandle] = useState<string | null>(null);
  const googleEnabled = isGoogleAuthEnabled();
  const [synced, setSynced] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      if (!store) return;
      const [storedGames, storedDrills] = await Promise.all([
        store.listGames(),
        store.listDrills(),
      ]);
      setGames(storedGames.length);
      setDrills(storedDrills.length);
    })();
  }, [store]);

  useEffect(() => {
    void (async () => {
      if (!auth.user) {
        setHandle(null);
        return;
      }
      const client = supabase();
      if (!client) return;
      const value = await ensureProfile(client, auth.user);
      setHandle(value);
    })();
  }, [auth.user]);

  const runSync = async () => {
    const client = supabase();
    if (!client || !auth.user) return;
    setBusy(true);
    setMessage(null);
    try {
      const gameResult = await syncGames(client, auth.user, store);
      const drillResult = await syncDrills(client, auth.user, store);
      if (gameResult.error || drillResult.error) {
        setMessage(gameResult.error ?? drillResult.error);
      } else {
        setSynced(
          `${gameResult.uploaded} games and ${drillResult.uploaded} drill positions uploaded`,
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await auth.sendMagicLink(email.trim());
      setSent(true);
      setMessage("Check your inbox for the sign-in link.");
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "Could not send the link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <h1 className="panel__title">Your account</h1>
          <span className="chip">
            <span className="chip__dot" />
            {auth.user ? "Signed in" : "Local only"}
          </span>
        </div>
        <div className="panel__body">
          {!auth.configured ? (
            <>
              <p className="notice">
                Accounts are not switched on for this deployment yet. Everything still works: analysis,
                opening drills, tactics and mistake mining all run on this device, with no account and no
                paywall.
              </p>
              <p className="notice">
                When accounts are enabled, signing in only syncs your progress between devices. It never
                unlocks a paid tier, because there is no paid tier.
              </p>
            </>
          ) : auth.user ? (
            <>
              <p className="notice">
                Signed in as <strong>{auth.user.email}</strong>
                {handle ? (
                  <>
                    {" "}
                    as <strong>@{handle}</strong>
                  </>
                ) : null}
                . Your progress syncs to your account and follows you to another device.
              </p>
              <div className="btnrow">
                <button className="btn btn--primary" disabled={busy} onClick={() => void runSync()}>
                  {busy ? "Syncing" : "Sync now"}
                </button>
                <button
                  className="btn btn--ghost"
                  onClick={() => {
                    void auth.signOut();
                  }}
                >
                  Sign out
                </button>
              </div>
              {synced ? <p className="notice">Synced: {synced}.</p> : null}
              {message ? <p className="notice notice--error">{message}</p> : null}
            </>
          ) : (
            <>
              <p className="notice">
                An account is optional. It only syncs your progress, so you can practise on a phone and
                pick up on a laptop.
              </p>

              <div className="field">
                <label className="field__label" htmlFor="auth-email">
                  Email
                </label>
                <input
                  id="auth-email"
                  className="input"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
                <span className="field__hint">We send a one-time link. No password to remember.</span>
              </div>

              {message ? <p className="notice">{message}</p> : null}

              <div className="btnrow">
                <button
                  className="btn btn--primary"
                  disabled={busy || !email.trim() || sent}
                  onClick={() => void submit()}
                >
                  {busy ? "Sending" : sent ? "Link sent" : "Email me a link"}
                </button>
                {googleEnabled ? (
                  <button
                    className="btn btn--ghost"
                    disabled={busy}
                    onClick={() => {
                      setBusy(true);
                      void auth
                        .signInWithGoogle()
                        .catch((cause: unknown) =>
                          setMessage(cause instanceof Error ? cause.message : "Could not sign in."),
                        )
                        .finally(() => setBusy(false));
                    }}
                  >
                    Continue with Google
                  </button>
                ) : null}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="panel">
        <div className="panel__head">
          <h1 className="panel__title">On this device</h1>
        </div>
        <div className="panel__body">
          <div className="metrics">
            <div className="metric">
              <span className="metric__label">Games analysed</span>
              <span className="metric__value metric__value--white">{games}</span>
              <span className="metric__note">stored locally</span>
            </div>
            <div className="metric">
              <span className="metric__label">Positions to drill</span>
              <span className="metric__value">{drills}</span>
              <span className="metric__note">from your games</span>
            </div>
          </div>
          <p className="notice">
            Analysis history, drill schedules and streaks are stored in this browser. Clear the site data to
            wipe them.
          </p>
        </div>
      </section>
    </div>
  );
}
